// Kiosk shell (epic E01/E10): device provisioning, the sync loop, fleet heartbeat and a hidden
// staff panel. The patient journey itself lives in Journey.tsx.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ThresholdSet } from '@shared';
import { ApiError, kioskApi, kioskCreds, wsUrl, type KioskCredentials } from '../lib/api.ts';
import { Realtime } from '../lib/realtime.ts';
import { bridgeCall, createAdapters, hasBridge, nativeSimulated, printerPaper, SCENARIOS, shellCaps, Simulator, type DeviceAdapter, type DeviceKind, type PaperState, type Scenario, type SelfTest } from './devices.ts';
import { Journey, type RxLine } from './Journey.tsx';
import { Outbox, type QueuedCommand, type SyncResult } from './outbox.ts';
import { setVoice, voiceEnabled } from './speech.ts';
import { GovHeader, NoticeBar, SiteFooter } from '../ui/site.tsx';

export const APP_VERSION = 'kiosk-web/0.1.0';

export interface KioskConfig {
  kioskId: string;
  serial: string;
  facility: { id: string; name: string; name_ur: string; type: string; district: string; district_ur: string; division: string };
  thresholds: ThresholdSet;
  consentVersion: string;
  config: { idleResetSeconds?: number; staffPin?: string; language?: string };
}

export interface KioskServices {
  config: KioskConfig;
  api: ReturnType<typeof kioskApi>;
  rt: Realtime;
  sim: Simulator;
  adapters: Record<DeviceKind, DeviceAdapter>;
  online: boolean;
  queueDepth: number;
  /** Queues a command (encrypted, ordered) and, unless told otherwise, tries to deliver it now. */
  send(type: string, payload: unknown, opts?: { flush?: boolean }): Promise<SyncResult | undefined>;
  flush(): Promise<void>;
  paperState(): Promise<PaperState>;
  reportSensors(s: Partial<Record<DeviceKind, SelfTest>>): void;
  rx: RxLine[];
  setRx(rx: RxLine[]): void;
  onBrandPress(): void;
  onBrandRelease(): void;
}

const CONFIG_CACHE = 'movox.kiosk.config';

export function KioskApp() {
  const [creds, setCreds] = useState<KioskCredentials | null>(() => kioskCreds.load());
  const [config, setConfig] = useState<KioskConfig | null>(() => {
    const raw = localStorage.getItem(CONFIG_CACHE);
    return raw ? (JSON.parse(raw) as KioskConfig) : null;
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('/sw.js').catch(() => {});
  }, []);

  // Managed devices receive their kiosk ID and key from the MDM (Android managed configuration).
  useEffect(() => {
    if (creds || !shellCaps()) return;
    bridgeCall<{ kioskId?: string; key?: string } | null>('shell.provisioning', {}, 5000)
      .then((p) => {
        if (p?.kioskId && p.key) {
          const c = { kioskId: p.kioskId, key: p.key };
          kioskCreds.save(c);
          setCreds(c);
        }
      })
      .catch(() => {});
  }, [creds]);

  useEffect(() => {
    if (!creds) return;
    kioskApi(creds)
      .get<KioskConfig>('/api/kiosk/config')
      .then((c) => {
        setConfig(c);
        localStorage.setItem(CONFIG_CACHE, JSON.stringify(c));
        setError('');
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          kioskCreds.clear();
          localStorage.removeItem(CONFIG_CACHE);
          setCreds(null);
          setConfig(null);
          setError('This kiosk ID / device key was not accepted.');
        }
        // Network errors: keep running on the cached configuration (offline-safe start).
      });
  }, [creds]);

  if (!creds || !config) return <Provision error={error} onSave={(c) => { kioskCreds.save(c); setCreds(c); }} waiting={Boolean(creds)} />;
  return <KioskRuntime creds={creds} config={config} onUnprovision={() => { kioskCreds.clear(); localStorage.removeItem(CONFIG_CACHE); setCreds(null); setConfig(null); }} />;
}

function Provision({ onSave, error, waiting }: { onSave: (c: KioskCredentials) => void; error: string; waiting: boolean }) {
  const [kioskId, setKioskId] = useState('AJK-K-001');
  const [key, setKey] = useState('');
  const demoKey = (id: string) => `demo-kiosk-key-${id.slice(-3)}`;
  return (
    <div className="site provision">
      <GovHeader homeLink={false} />
      <NoticeBar
        badge="ENGINEER SET-UP"
        items={[
          'Kiosk commissioning — authorised installation engineers only',
          'Each Model H unit is registered once to its health facility',
          'Demonstration build · synthetic AJK pilot dataset',
        ]}
        aside="Model H · 32″ kiosk"
      />
      <main className="provision-band">
        <form className="provision-card" onSubmit={(e) => { e.preventDefault(); if (key) onSave({ kioskId, key }); }}>
          <div className="provision-head">
            <img src="/brand/seal.svg" alt="" />
            <div>
              <div className="gov-kicker">Smart Health Kiosk Program</div>
              <h1>Kiosk registration</h1>
              <div className="provision-ur" lang="ur" dir="rtl">کیوسک کی رجسٹریشن</div>
            </div>
          </div>
          <div className="gov-rule" />
          <p className="provision-lede">Register this Model H unit to its health facility with the kiosk ID and device key issued for it. This is done once, by the installation engineer, at commissioning.</p>
          {waiting && !error && <div className="callout info">Connecting to the program server…</div>}
          {error && <div className="callout provision-error" role="alert">{error}</div>}
          <label>Kiosk ID
            <select className="input" value={kioskId} onChange={(e) => setKioskId(e.target.value)}>
              {Array.from({ length: 25 }, (_, i) => `AJK-K-${String(i + 1).padStart(3, '0')}`).map((id) => <option key={id}>{id}</option>)}
            </select>
          </label>
          <label>Device key
            <input className="input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Issued with the unit" autoComplete="off" />
          </label>
          <div className="provision-actions">
            <button type="button" className="btn btn-lg provision-demo" onClick={() => setKey(demoKey(kioskId))}>Use demonstration key</button>
            <button type="submit" className="btn btn-lg btn-green" disabled={!key}>Register kiosk</button>
          </div>
          <div className="provision-foot">
            <span>Demonstration keys work only with the synthetic AJK pilot dataset.</span>
            <span>Department of Health · Government of Azad Jammu &amp; Kashmir</span>
          </div>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
}

function KioskRuntime({ creds, config, onUnprovision }: { creds: KioskCredentials; config: KioskConfig; onUnprovision: () => void }) {
  const api = useMemo(() => kioskApi(creds), [creds]);
  const outbox = useMemo(() => new Outbox(creds.kioskId), [creds.kioskId]);
  const rt = useMemo(() => new Realtime(wsUrl({ kiosk: creds.kioskId, key: creds.key })), [creds]);
  const sim = useMemo(() => new Simulator(), []);
  const adapters = useMemo(() => createAdapters(sim), [sim]);
  const [netOnline, setNetOnline] = useState(navigator.onLine);
  const [forcedOffline, setForcedOffline] = useState(false);
  const [serverReachable, setServerReachable] = useState(true);
  const [queueDepth, setQueueDepth] = useState(0);
  const [paper, setPaper] = useState<PaperState>('ok');
  const [rx, setRx] = useState<RxLine[]>([]);
  const [panel, setPanel] = useState(false);
  const sensors = useRef<Partial<Record<DeviceKind, SelfTest>>>({});
  const pressTimer = useRef<number | null>(null);
  const online = netOnline && !forcedOffline && serverReachable;
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const forcedRef = useRef(forcedOffline);
  forcedRef.current = forcedOffline;

  useEffect(() => () => rt.close(), [rt]);
  useEffect(() => outbox.onChange(setQueueDepth), [outbox]);
  useEffect(() => {
    void outbox.depth().then(setQueueDepth);
    const on = () => setNetOnline(true);
    const off = () => setNetOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [outbox]);

  const deliver = useCallback(async (cmds: QueuedCommand[]) => {
    if (forcedRef.current) throw new Error('offline (simulated)');
    try {
      const r = await api.post<{ results: SyncResult[] }>('/api/kiosk/sync', { commands: cmds }, AbortSignal.timeout(8000));
      setServerReachable(true);
      return r.results;
    } catch (e) {
      if (!(e instanceof ApiError)) setServerReachable(false);
      throw e;
    }
  }, [api]);

  const flush = useCallback(async () => {
    await outbox.flush(deliver).catch(() => new Map());
  }, [deliver, outbox]);

  const send = useCallback(async (type: string, payload: unknown, opts: { flush?: boolean } = {}) => {
    const cmd = await outbox.enqueue(type, payload);
    if (opts.flush === false || !onlineRef.current) return undefined;
    const results = await outbox.flush(deliver).catch(() => new Map<string, SyncResult>());
    return results.get(cmd.key);
  }, [deliver, outbox]);

  // Background sync and heartbeat.
  useEffect(() => {
    const syncIv = setInterval(() => void flush(), 15000);
    const beat = async () => {
      if (forcedRef.current) return;
      const conn = (navigator as unknown as { connection?: { type?: string } }).connection;
      const shell = shellCaps() ? await bridgeCall<{ version: string; network: string; power: string }>('shell.info', {}, 3000).catch(() => null) : null;
      try {
        await api.post('/api/kiosk/heartbeat', {
          appVersion: shell ? `${APP_VERSION} android/${shell.version}` : APP_VERSION,
          network: shell?.network ?? (conn?.type === 'cellular' ? 'mobile' : conn?.type === 'ethernet' ? 'ethernet' : 'wifi'),
          printerPaper: await printerPaper(paper),
          power: shell?.power ?? 'mains',
          sensors: Object.fromEntries(Object.entries(sensors.current).map(([k, v]) => [k === 'body' ? 'scale' : k === 'oximeter' ? 'spo2' : k, v])),
          queueDepth: await outbox.depth(),
        }, AbortSignal.timeout(8000));
        setServerReachable(true);
        void flush();
      } catch {
        setServerReachable(false);
      }
    };
    void beat();
    const beatIv = setInterval(beat, 60000);
    return () => {
      clearInterval(syncIv);
      clearInterval(beatIv);
    };
  }, [api, flush, outbox, paper]);

  useEffect(() => {
    if (online) void flush();
  }, [online, flush]);

  const svc: KioskServices = {
    config, api, rt, sim, adapters, online, queueDepth, send, flush,
    paperState: () => printerPaper(paper),
    reportSensors: (s) => { sensors.current = s; },
    rx, setRx,
    onBrandPress: () => { pressTimer.current = window.setTimeout(() => setPanel(true), 2500); },
    onBrandRelease: () => { if (pressTimer.current) clearTimeout(pressTimer.current); },
  };

  return (
    <>
      <Journey svc={svc} />
      {panel && (
        <StaffPanel
          svc={svc}
          pin={String(config.config.staffPin ?? '1234')}
          forcedOffline={forcedOffline}
          onForceOffline={setForcedOffline}
          paper={paper}
          onPaper={setPaper}
          outbox={outbox}
          onClose={() => setPanel(false)}
          onUnprovision={onUnprovision}
        />
      )}
    </>
  );
}

function StaffPanel(props: {
  svc: KioskServices; pin: string; forcedOffline: boolean; onForceOffline: (v: boolean) => void; paper: PaperState; onPaper: (p: PaperState) => void;
  outbox: Outbox; onClose: () => void; onUnprovision: () => void;
}) {
  const { svc } = props;
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState('');
  const [scenario, setScenario] = useState<Scenario>(svc.sim.scenario);
  const [dead, setDead] = useState<{ key: string; error?: string; queuedAt: string }[]>([]);
  const [voice, setVoiceState] = useState(voiceEnabled());
  useEffect(() => {
    if (unlocked) void props.outbox.deadLetters().then(setDead);
  }, [unlocked, props.outbox]);
  return (
    <div className="k-modal staff-panel" dir="ltr" lang="en">
      <div className="k-modal-card">
        <div className="staff-panel-head">
          <img src="/brand/seal.svg" alt="" />
          <div>
            <h2>Staff &amp; engineer panel</h2>
            <div className="staff-panel-sub">Authorised personnel only · Department of Health, Government of AJK</div>
          </div>
        </div>
        {!unlocked ? (
          <div className="row">
            <input className="input" type="password" inputMode="numeric" placeholder="Staff PIN" value={pin} onChange={(e) => setPin(e.target.value)} />
            <button className="btn btn-primary" onClick={() => setUnlocked(pin === props.pin)}>Unlock</button>
            <button className="btn" onClick={props.onClose}>Close</button>
          </div>
        ) : (
          <div className="panel-grid">
            <div><strong>Kiosk</strong> {svc.config.kioskId} · {svc.config.serial}<br /><span className="muted">{svc.config.facility.name} · {svc.config.facility.district}</span></div>
            <div><strong>Devices</strong> {!hasBridge() ? 'Browser simulator' : nativeSimulated() ? 'Android shell · built-in device simulator' : 'Android shell · Model H peripherals'}
              {shellCaps() && <span className="muted"> · shell {shellCaps()!.version} · printer {shellCaps()!.printer}{shellCaps()!.lockTask ? ' · lock-task' : ''}</span>}</div>
            <div><strong>Sync</strong> {svc.online ? 'online' : 'offline'} · {svc.queueDepth} queued · {dead.length} rejected
              <button className="btn btn-small" onClick={() => void svc.flush()}>Sync now</button></div>
            <label className="check"><input type="checkbox" checked={props.forcedOffline} onChange={(e) => props.onForceOffline(e.target.checked)} /> Simulate network loss (offline-safe demo)</label>
            {(!hasBridge() || nativeSimulated()) && (
              <label>Simulated readings
                <select className="input" value={scenario} onChange={(e) => { const v = e.target.value as Scenario; svc.sim.scenario = v; setScenario(v); if (hasBridge()) void bridgeCall('sim.setScenario', { scenario: v }).catch(() => {}); }}>
                  {SCENARIOS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
              </label>
            )}
            {(!hasBridge() || nativeSimulated()) && (
              <label>Printer paper
                <select className="input" value={props.paper} onChange={(e) => { const p = e.target.value as PaperState; props.onPaper(p); if (hasBridge()) void bridgeCall('sim.setPaper', { paper: p }).catch(() => {}); }}>
                  {['ok', 'low', 'out'].map((p) => <option key={p}>{p}</option>)}
                </select>
              </label>
            )}
            <label className="check"><input type="checkbox" checked={voice} onChange={(e) => { setVoice(e.target.checked); setVoiceState(e.target.checked); }} /> Voice guidance</label>
            <div><strong>Thresholds</strong> {svc.config.thresholds.version} ({svc.config.thresholds.status})</div>
            {dead.length > 0 && <details><summary>Rejected commands</summary><ul className="small">{dead.map((d) => <li key={d.key}>{d.key}: {d.error}</li>)}</ul></details>}
            <div className="row">
              <button className="btn btn-primary" onClick={props.onClose}>Done</button>
              <button className="btn btn-danger" onClick={() => { if (confirm('Remove this kiosk’s credentials?')) props.onUnprovision(); }}>Unprovision</button>
              {shellCaps() && <button className="btn" onClick={() => { if (confirm('Leave kiosk mode on this device?')) void bridgeCall('shell.exitKiosk', {}).catch(() => {}); }}>Exit kiosk mode</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
